import { useState } from "react";
import { useActiveCall } from "@/lib/activeCall";
import { useRecordingConsent } from "@/hooks/useRecordingConsent";
import { Button } from "@/components/ui/button";
import { ResponsiveDialog } from "@/components/ui/responsive-dialog";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

/**
 * Renders inside the GlobalVideoCallOverlay whenever there is an active call.
 * Only shows the client consent dialog; coach controls live inline in the
 * Canvas header via RecordingCoachControls.
 */
export function RecordingConsentPanel() {
  const { activeCall } = useActiveCall();
  const { toast } = useToast();
  const {
    recordingState,
    hasGranted,
    loading,
    grantConsent,
  } = useRecordingConsent(activeCall?.sessionId ?? null);
  const [declined, setDeclined] = useState(false);

  if (!activeCall || activeCall.isCoach) return null;

  const open = recordingState === "consent_pending" && !hasGranted && !declined;
  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={(o) => { if (!o) setDeclined(true); }}
      title="Aufzeichnung der Coaching-Sitzung"
      description="Dein Coach möchte diese Sitzung aufzeichnen. Die Aufzeichnung wird nur mit deiner ausdrücklichen Zustimmung gestartet."
    >
      <div className="space-y-4 px-1 pb-1 text-sm text-muted-foreground">
        <p>
          Dein Coach wählt beim Start, ob nur der Ton oder Ton und Video
          aufgezeichnet werden. Die Aufnahme wird verschlüsselt gespeichert und
          nur für die in der Datenschutzerklärung beschriebenen Coaching-Zwecke
          verwendet (Transkripte, Zusammenfassungen, Nachbereitung).
        </p>
        <p>
          Du kannst die Zustimmung jederzeit ablehnen, die Sitzung läuft
          in beiden Fällen normal weiter, nur ohne Aufzeichnung.
        </p>
        <p>
          Die Aufnahme wird transkribiert und danach beim Aufzeichnungsanbieter
          gelöscht. An die KI geht nur eine bereinigte Fassung ohne Namen,
          Firmen oder Kontaktdaten. Details:{" "}
          <a
            href="/privacy#recording"
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary underline underline-offset-2"
          >
            Datenschutzerklärung (Abschnitt 7.6)
          </a>
          .
        </p>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="ghost" onClick={() => setDeclined(true)} disabled={loading}>
            Ablehnen
          </Button>
          <Button
            onClick={async () => {
              const r = await grantConsent();
              if (!r.ok) {
                toast({ title: "Fehler", description: r.error, variant: "destructive" });
              } else {
                toast({ title: "Zustimmung erteilt" });
              }
            }}
            disabled={loading}
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Aufzeichnung erlauben
          </Button>
        </div>
      </div>
    </ResponsiveDialog>
  );
}